import { useState, useCallback, useEffect } from 'react';
import { CardTransaction } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bridge/resources/bridgeApi/types';
import { formatErrorMessage } from 'utils/error';
import theraToast from 'components/generic-components/toast/TheraToast';

interface UseCardTransactionsProps {
  theraFernApi: any;
  customerId?: string;
  cardAccountId?: string;
}

interface UseCardTransactionsReturn {
  transactions: CardTransaction[] | null;
  isFetching: boolean;
  fetchCardTransactions: () => Promise<void>;
}

/**
 * Custom hook for fetching card transactions
 * @param theraFernApi - The Thera Fern API client
 * @param customerId - The Bridge customer ID
 * @param cardAccountId - The card account ID
 * @returns Transactions, loading state, and fetch function
 */
export const useCardTransactions = ({
  theraFernApi,
  customerId,
  cardAccountId,
}: UseCardTransactionsProps): UseCardTransactionsReturn => {
  const [transactions, setTransactions] = useState<CardTransaction[] | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const fetchCardTransactions = useCallback(async () => {
    if (!customerId || !cardAccountId) {
      setTransactions(null);
      return;
    }

    try {
      setIsFetching(true);
      const transactionsResult = await theraFernApi.thera.bridge.bridgeApi.cardTransactions(customerId, cardAccountId);
      setTransactions(transactionsResult.data);
    } catch (error) {
      const errorMessage = formatErrorMessage(error, 'Failed to fetch card transactions');
      theraToast.error({
        message: errorMessage,
        error: error,
        options: {
          id: 'failed-to-fetch-card-transactions',
        },
      });
    } finally {
      setIsFetching(false);
    }
  }, [cardAccountId, customerId, theraFernApi]);

  useEffect(() => {
    if (customerId && cardAccountId) {
      fetchCardTransactions();
    } else {
      setTransactions(null);
    }
  }, [cardAccountId, customerId, fetchCardTransactions]);

  return {
    transactions,
    isFetching,
    fetchCardTransactions,
  };
};
